"use client"

import { useId, useState } from "react"
import { Formik, Form } from "formik"
import { Check, ChevronsUpDown, HelpCircle, Loader2, Pencil } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { FormInput } from "@/components/form-inputs"
import { useAgentRolesQuery } from "@/hooks/queries/ai-use-case-roles"
import { DEFAULT_ROLE_SLUG, MAX_AGENT_ROLES, ROLE_LABEL_MAX_LENGTH, isCustomOnly } from "@/lib/ai/roles/types"
import type { AiUseCaseRoleView } from "@/server-actions/ai-use-case-roles"

const ROLE_CHIPS_CONTENT_CLASS = "min-h-[104px]"

interface RoleMultiSelectProps {
  /** Primary use case of the channel (e.g. email_auto_reply) whose roles populate the list. */
  useCase: string
  value: string[]
  /** Second argument carries the full roles (e.g. for prompt prefill). */
  onChange: (roles: string[], roleData: AiUseCaseRoleView[]) => void
  /** Custom-role display name; the naming affordance renders only for the custom-only set. */
  roleLabel?: string
  onRoleLabelChange?: (label: string) => void
  /** When set (settings mode), the name dialog persists immediately via this callback. */
  onRoleLabelCommit?: (label: string) => Promise<void>
  /** True while a role change is being applied server-side (shows a spinner, disables the control). */
  loading?: boolean
  /** Draw attention to the required selector until a role is chosen. */
  attention?: boolean
  /** Marks the label after a failed validation attempt without stopping its attention animation. */
  invalid?: boolean
  disabled?: boolean
  id?: string
  /** Onboarding exposes every role immediately; settings retain the compact popover. */
  presentation?: "popover" | "chips"
}

/**
 * Agent role selector shared by all channels. An agent can carry up to
 * {@link MAX_AGENT_ROLES} roles, whose presets are merged into one prompt.
 * Custom is the "write your own" starter, so it is exclusive — combining it with
 * a real role has no meaning.
 */
export function RoleMultiSelect({
  useCase,
  value,
  onChange,
  roleLabel,
  onRoleLabelChange,
  onRoleLabelCommit,
  loading,
  disabled,
  attention = false,
  invalid = false,
  id,
  presentation = "popover",
}: RoleMultiSelectProps) {
  const { data: roles = [], isLoading } = useAgentRolesQuery(useCase)
  const [nameDialogOpen, setNameDialogOpen] = useState(false)
  const [open, setOpen] = useState(false)
  const generatedId = useId()
  const controlId = id ?? `agent-roles-${generatedId}`
  // Committed on close: per-checkbox commits would mean one confirmation and one
  // billed composition per toggle.
  const [draft, setDraft] = useState<string[]>(value)

  const customLabel = roleLabel?.trim()
  const showNaming = onRoleLabelChange !== undefined && isCustomOnly(value)
  const atLimit = draft.length >= MAX_AGENT_ROLES

  const displayName = (role: AiUseCaseRoleView) =>
    role.slug === DEFAULT_ROLE_SLUG && customLabel ? customLabel : role.name

  const selectedRoles = value
    .map((slug) => roles.find((r) => r.slug === slug))
    .filter((role): role is AiUseCaseRoleView => role !== undefined)

  const toggleDraftRole = (slug: string) => {
    setDraft((current) => getNextRoleSelection(current, slug))
  }

  const toggleChipRole = (slug: string) => {
    const next = getNextRoleSelection(value, slug)
    if (haveSameRoles(next, value)) return
    onChange(next, getSelectedRoleData(next, roles))
  }

  const openChanged = (next: boolean) => {
    setOpen(next)
    if (next) {
      setDraft(value)
      return
    }
    // Commit on close: one change event per editing session.
    const changed = draft.length !== value.length || draft.some((slug) => !value.includes(slug))
    if (!changed) return
    onChange(draft, getSelectedRoleData(draft, roles))
  }

  const triggerLabel = getRoleTriggerLabel(isLoading, selectedRoles, displayName)

  return (
    <div className="space-y-2">
      {/* Label must stay inline (like the sibling fields' labels) or the grid rows misalign by the parent line-box strut. */}
      <Label
        htmlFor={controlId}
        className={`${attention ? "animate-[pulse_1.4s_ease-in-out_infinite]" : ""} ${
          invalid ? "font-bold text-red-800 dark:text-red-300" : ""
        }`}
      >
        <span>Select agent roles</span>
        {loading && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" />}
      </Label>
      <TooltipProvider delayDuration={250}>
        {presentation === "chips" ? (
          <RoleChips
            id={controlId}
            roles={roles}
            selectedRoles={value}
            displayName={displayName}
            isLoading={isLoading}
            disabled={Boolean(disabled || isLoading || loading)}
            onToggle={toggleChipRole}
          />
        ) : (
          <Popover open={open} onOpenChange={openChanged}>
            <PopoverTrigger asChild>
              <Button
                id={controlId}
                type="button"
                variant="outline"
                className="w-full justify-between font-normal"
                disabled={disabled || isLoading || loading}
              >
                <span className="truncate">{triggerLabel}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-2">
              <div className="flex flex-col gap-1">
                {roles.map((role) => {
                  const checked = draft.includes(role.slug)
                  // Custom is never blocked — picking it always clears the rest.
                  const blocked =
                    !checked && atLimit && role.slug !== DEFAULT_ROLE_SLUG && !draft.includes(DEFAULT_ROLE_SLUG)
                  return (
                    <div
                      key={role.slug}
                      role="checkbox"
                      aria-checked={checked}
                      aria-disabled={blocked}
                      tabIndex={blocked ? -1 : 0}
                      onClick={(event) => {
                        event.preventDefault()
                        if (!blocked) toggleDraftRole(role.slug)
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return
                        event.preventDefault()
                        if (!blocked) toggleDraftRole(role.slug)
                      }}
                      className={`flex items-start gap-2 rounded-sm px-2 py-1.5 text-sm font-normal ${
                        blocked ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-muted"
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={blocked}
                        aria-hidden
                        tabIndex={-1}
                        className="pointer-events-none mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-1">
                          <div className="truncate">{displayName(role)}</div>
                          {role.description ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label={`About ${displayName(role)} role`}
                                  className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <HelpCircle className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px] text-xs">
                                {role.description}
                              </TooltipContent>
                            </Tooltip>
                          ) : null}
                        </div>
                        {role.description ? (
                          <div className="truncate text-xs text-muted-foreground">{role.description}</div>
                        ) : null}
                      </div>
                      {checked ? (
                        <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      ) : null}
                    </div>
                  )
                })}
              </div>
              <p className="px-2 pt-2 text-xs text-muted-foreground">
                {`Up to ${MAX_AGENT_ROLES} roles; their prompts are merged into one when you close this list. "Custom" cannot be combined with other roles.`}
              </p>
            </PopoverContent>
          </Popover>
        )}
      </TooltipProvider>

      <div className="flex min-h-7 items-start justify-between gap-2">
        {presentation === "popover" ? (
          <div className="flex flex-wrap gap-1">
            {selectedRoles.map((role) => (
              <Badge key={role.slug} variant="secondary" className="font-normal">
                {displayName(role)}
              </Badge>
            ))}
          </div>
        ) : <div />}
        {showNaming && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 shrink-0 gap-1 px-2 text-muted-foreground"
            disabled={disabled}
            onClick={() => setNameDialogOpen(true)}
          >
            <Pencil className="h-3 w-3" />
            <span>{customLabel ? "Rename" : "Name role"}</span>
          </Button>
        )}
      </div>

      {showNaming && (
        <RoleNameDialog
          open={nameDialogOpen}
          onOpenChange={setNameDialogOpen}
          initialLabel={customLabel ?? ""}
          onSubmit={async (label) => {
            await onRoleLabelCommit?.(label)
            onRoleLabelChange?.(label)
          }}
        />
      )}
    </div>
  )
}

function RoleChips({
  id,
  roles,
  selectedRoles,
  displayName,
  isLoading,
  disabled,
  onToggle,
}: {
  id: string
  roles: AiUseCaseRoleView[]
  selectedRoles: string[]
  displayName: (role: AiUseCaseRoleView) => string
  isLoading: boolean
  disabled: boolean
  onToggle: (slug: string) => void
}) {
  if (roles.length === 0) {
    return (
      <div
        id={id}
        className={`${ROLE_CHIPS_CONTENT_CLASS} flex items-center justify-center text-center`}
      >
        <p className="text-sm text-muted-foreground">
          {isLoading ? "Loading roles..." : "No role options are available."}
        </p>
      </div>
    )
  }

  return (
    <div className={`${ROLE_CHIPS_CONTENT_CLASS} space-y-2`}>
      <div
        id={id}
        className="flex flex-wrap justify-center gap-2"
        role="group"
        aria-label="Agent role options"
      >
        {roles.map((role) => {
          const selected = selectedRoles.includes(role.slug)
          const blocked = isRoleSelectionBlocked(selectedRoles, role.slug)
          const chip = (
            <Button
              key={role.slug}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={selected}
              aria-disabled={blocked || undefined}
              disabled={disabled}
              className={`h-auto min-h-8 rounded-full px-3 py-1 text-xs font-normal ${getRoleChipStateClass(selected, blocked)}`}
              onClick={() => onToggle(role.slug)}
            >
              {selected ? <Check className="mr-1 h-3.5 w-3.5" aria-hidden /> : null}
              {displayName(role)}
              {role.description ? <HelpCircle className="ml-1 h-3.5 w-3.5 opacity-60" aria-hidden /> : null}
            </Button>
          )

          if (!role.description) return chip
          return (
            <Tooltip key={role.slug}>
              <TooltipTrigger asChild>{chip}</TooltipTrigger>
              <TooltipContent className="max-w-[240px] text-xs">
                {role.description}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
      <p className="text-center text-xs leading-relaxed text-muted-foreground">
        {`Choose up to ${MAX_AGENT_ROLES} roles. "Custom" cannot be combined with other roles.`}
      </p>
    </div>
  )
}

function getNextRoleSelection(current: string[], slug: string) {
  if (current.includes(slug)) {
    // Never leave the agent role-less — the last one cannot be unchecked.
    return current.length === 1
      ? current
      : current.filter((selectedSlug) => selectedSlug !== slug)
  }
  // Custom replaces everything, and any real role replaces Custom.
  if (slug === DEFAULT_ROLE_SLUG) return [DEFAULT_ROLE_SLUG]

  const withoutCustom = current.filter(
    (selectedSlug) => selectedSlug !== DEFAULT_ROLE_SLUG,
  )
  if (withoutCustom.length >= MAX_AGENT_ROLES) return current
  return [...withoutCustom, slug]
}

function isRoleSelectionBlocked(selectedRoles: string[], slug: string) {
  if (selectedRoles.includes(slug) || slug === DEFAULT_ROLE_SLUG) return false
  const selectedPresetCount = selectedRoles.filter(
    (selectedSlug) => selectedSlug !== DEFAULT_ROLE_SLUG,
  ).length
  return selectedPresetCount >= MAX_AGENT_ROLES
}

function getRoleChipStateClass(selected: boolean, blocked: boolean) {
  if (blocked) return "cursor-not-allowed opacity-50"
  if (selected) {
    return "border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
  }
  return "hover:border-blue-300 hover:bg-blue-50/60"
}

function haveSameRoles(left: string[], right: string[]) {
  return left.length === right.length && left.every((slug) => right.includes(slug))
}

function getSelectedRoleData(selectedRoles: string[], roles: AiUseCaseRoleView[]) {
  return selectedRoles
    .map((slug) => roles.find((role) => role.slug === slug))
    .filter((role): role is AiUseCaseRoleView => role !== undefined)
}

function getRoleTriggerLabel(
  isLoading: boolean,
  selectedRoles: AiUseCaseRoleView[],
  displayName: (role: AiUseCaseRoleView) => string,
) {
  if (isLoading) return "Loading roles..."
  if (selectedRoles.length > 0) return selectedRoles.map(displayName).join(", ")
  return "Select roles"
}

interface RoleNameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialLabel: string
  onSubmit: (label: string) => Promise<void>
}

/** Names the tenant's custom role — the name replaces "Custom" wherever the role is shown. */
function RoleNameDialog({ open, onOpenChange, initialLabel, onSubmit }: RoleNameDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Name Your Role</DialogTitle>
          <DialogDescription>
            Give this agent's custom role a name — it will be shown instead of "Custom". Leave
            empty to keep "Custom".
          </DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{ label: initialLabel }}
          enableReinitialize
          onSubmit={async ({ label }, { setStatus }) => {
            try {
              await onSubmit(label.trim())
              onOpenChange(false)
            } catch (error) {
              setStatus(error instanceof Error ? error.message : "Failed to save role name")
            }
          }}
        >
          {({ isSubmitting, status }) => (
            <Form className="space-y-4">
              {status && <p className="text-sm font-medium text-destructive">{status}</p>}
              <FormInput
                name="label"
                label="Role name"
                placeholder="e.g. Consultant"
                maxLength={ROLE_LABEL_MAX_LENGTH}
                showLength
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
